import { createClient } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueries, useQueryClient } from '@tanstack/react-query'

import { getGetProjectQueryKey, getListProjectsQueryKey } from '@/api/assessments.hook'
import { getListInherentRiskCategoriesQueryKey } from '@/api/company.hook'
import { getListControlsQueryKey } from '@/api/control.hook'
import {
  createScope,
  deleteScope,
  getScope,
  listScopes,
  setScopeControls,
  updateScopeDefinition,
} from '@/gen/inventory/v1/scope_service-ScopeService_connectquery'
import { GetScopeResponse, ScopeService } from '@/gen/inventory/v1/scope_service_pb'

import { sessionTokenInterceptor } from '@/lib/auth/session-token-interceptor'
import { defaultTransport, getConnectTransport } from '@/lib/connect/connect-transport'

const scopesClient = createClient(
  ScopeService,
  getConnectTransport({
    interceptors: [sessionTokenInterceptor],
  }),
)

export const getListScopesQueryKey = () =>
  createConnectQueryKey({
    schema: ScopeService.method.listScopes,
    input: {},
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const getGetScopeQueryKey = (id?: string) =>
  createConnectQueryKey({
    schema: ScopeService.method.getScope,
    input: { id },
    transport: defaultTransport,
    cardinality: 'finite',
  })

export const useListScopes = () => {
  const queryState = useQuery(listScopes)

  return {
    ...queryState,
    data: queryState.data?.scopes ?? [],
  }
}

export const useGetScope = (id: string) => {
  const queryState = useQuery(getScope, { id })

  return {
    ...queryState,
    data: queryState.data,
  }
}

export const useCreateScope = () => {
  const queryClient = useQueryClient()

  return useMutation(createScope, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListScopesQueryKey() })
    },
  })
}

export const useUpdateScopeDefinition = () => {
  const queryClient = useQueryClient()

  return useMutation(updateScopeDefinition, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListInherentRiskCategoriesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetProjectQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListProjectsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListScopesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetScopeQueryKey() })
    },
  })
}

export const useDeleteScope = () => {
  const queryClient = useQueryClient()

  return useMutation(deleteScope, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListScopesQueryKey() })
    },
  })
}

export const useSetScopeControls = () => {
  const queryClient = useQueryClient()

  return useMutation(setScopeControls, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListControlsQueryKey() })
      queryClient.invalidateQueries({ queryKey: getListScopesQueryKey() })
      queryClient.removeQueries({ queryKey: getGetScopeQueryKey() })
    },
  })
}

export const useGetAllScopes = () => {
  const { data: scopes, isPending: listPending } = useListScopes()

  const queries = useQueries({
    queries: scopes.map((scope) => ({
      queryKey: getGetScopeQueryKey(scope.id),
      queryFn: () => scopesClient.getScope({ id: scope.id }),
    })),
  })

  return {
    isPending: listPending || queries.some((query) => query.isPending),
    isError: queries.some((query) => query.isError),
    data: queries.map((query) => query.data as GetScopeResponse | undefined),
    queries,
  }
}
